import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export interface SubscriptionStatus {
  subscribed: boolean;
  hasAccess: boolean;
  subscriptionStatus: string;
  subscriptionEnd?: string;
  productId?: string;
  stripeCustomerId?: string;
  loading: boolean;
  error: string | null;
}

export const useSubscription = () => {
  const { user, session } = useAuth();
  const [subscription, setSubscription] = useState<SubscriptionStatus>({
    subscribed: false,
    hasAccess: false,
    subscriptionStatus: 'inactive',
    loading: true,
    error: null,
  });

  const getAccessToken = useCallback(async () => {
    // Always prefer a fresh session from the auth client (context session can be stale)
    const { data, error } = await supabase.auth.getSession();
    if (error) throw error;
    const accessToken = data.session?.access_token;
    if (!accessToken) throw new Error('Not authenticated');
    return accessToken;
  }, []);

  const checkSubscription = useCallback(async () => {
    if (!user || !session) {
      setSubscription(prev => ({ ...prev, loading: false }));
      return;
    }

    try {
      setSubscription(prev => ({ ...prev, loading: true, error: null }));

      const accessToken = await getAccessToken();

      const { data, error } = await supabase.functions.invoke('check-subscription', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (error) {
        console.error('Subscription check error:', error);
        setSubscription(prev => ({
          ...prev,
          loading: false,
          error: error.message,
        }));
        return;
      }

      setSubscription({
        subscribed: data.subscribed || false,
        hasAccess: data.has_access || false,
        subscriptionStatus: data.subscription_status || 'inactive',
        subscriptionEnd: data.subscription_end,
        productId: data.product_id,
        stripeCustomerId: data.stripe_customer_id,
        loading: false,
        error: null,
      });
    } catch (err) {
      console.error('Subscription check failed:', err);
      setSubscription(prev => ({
        ...prev,
        loading: false,
        error: err instanceof Error ? err.message : 'Unknown error',
      }));
    }
  }, [user, session, getAccessToken]);

  // Check subscription on mount and when user changes
  useEffect(() => {
    if (user && session) {
      checkSubscription();
    }
  }, [user, session, checkSubscription]);

  // Auto-refresh every minute
  useEffect(() => {
    if (!user || !session) return;

    const interval = setInterval(checkSubscription, 60000);
    return () => clearInterval(interval);
  }, [user, session, checkSubscription]);

  const createCheckoutSession = async (plan: 'monthly' | 'lifetime' = 'monthly') => {
    const accessToken = await getAccessToken();

    const { data, error } = await supabase.functions.invoke('create-checkout', {
      body: { plan },
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (error) throw new Error(error.message);
    if (data?.error) throw new Error(data.error);
    if (data?.url) window.location.href = data.url;
    return data;
  };

  const createCreditCheckout = async (pack: 'starter' | 'growth' | 'pro' | 'elite') => {
    const accessToken = await getAccessToken();

    const { data, error } = await supabase.functions.invoke('create-credit-checkout', {
      body: { pack },
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (error) throw new Error(error.message);
    if (data?.error) throw new Error(data.error);
    if (data?.url) window.location.href = data.url;
    return data;
  };

  const openCustomerPortal = async () => {
    const accessToken = await getAccessToken();

    const { data, error } = await supabase.functions.invoke('customer-portal', {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    // The edge function returns JSON with error field even on 500 status
    // Check data.error first since that's where the actual message is
    if (data?.error) {
      // Pass through the specific error identifiers
      if (data.error.includes('TEST_ACCOUNT')) {
        throw new Error('TEST_ACCOUNT: Billing management is not available for test accounts.');
      }
      if (data.error.includes('MANUAL_ACCOUNT')) {
        throw new Error('MANUAL_ACCOUNT: Your account was created manually. Please contact support to manage your subscription.');
      }
      if (data.error.includes('No Stripe customer')) {
        throw new Error('MANUAL_ACCOUNT: No billing record found. Please contact support.');
      }
      throw new Error(data.error);
    }

    if (error) {
      throw new Error(error.message);
    }

    if (data?.url) {
      window.open(data.url, '_blank');
    }

    return data;
  };

  return {
    ...subscription,
    checkSubscription,
    createCheckoutSession,
    createCreditCheckout,
    openCustomerPortal,
  };
};
